// Inicio: el área central TRANQUILA cuando no hay nada abierto (como Random: gran espacio libre hasta
// que se abre una operación). Solo un logo discreto, los pendientes que de verdad tienen algo y lo
// abierto recientemente. Sin tarjetas ni gráficos: lo que está en cero no aparece. Cada línea abre
// donde se trabaja. Los conteos salen de las consultas reales del ERP (nada se calcula aquí).
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/cliente';
import type { DocumentoDteListado } from '../api/contratos';
import { TAMANO_PAGINA, useNotas, useSolicitudesCambio } from '../api/consultas';
import { useSesion } from '../sesion/SesionContexto';
import type { Reciente } from '../shell/preferencias';

/** Cuántos trae la primera página: si viene llena, «50+» (no se inventa el total). */
const cuenta = (n: number) => (n >= TAMANO_PAGINA ? `${TAMANO_PAGINA}+` : String(n));

interface Linea {
  id: string;
  texto: string;
  n: number | undefined;
  tono?: 'firma' | 'malo';
  alAbrir: () => void;
}

export function CentroOperativo({
  abrirRuta,
  abrirNotas,
  recientes,
  alAbrirReciente,
}: {
  abrirRuta: (ruta: string) => void;
  /** Consulta de notas filtrada por estado. */
  abrirNotas: (estado: string) => void;
  recientes: Reciente[];
  alAbrirReciente: (r: Reciente) => void;
}) {
  const { puede, empresa } = useSesion();
  const e = empresa!.id;

  const firmas = useNotas(e, { estado: 'EN_APROBACION', pagina: 1 }, puede('ventas.nota.aprobar'));
  const borradores = useNotas(e, { estado: 'BORRADOR', pagina: 1 }, puede('ventas.nota.ver'));
  const confirmadas = useNotas(
    e,
    { estado: 'CONFIRMADA', pagina: 1 },
    puede('ventas.nota.ver') && puede('ventas.despacho.ver'),
  );
  const solicitudes = useSolicitudesCambio(
    e,
    { estado: 'PENDIENTE' },
    puede('entidades.ficha.aprobar_cambio'),
  );
  const dte = useQuery({
    queryKey: ['emp', e, 'inicio-dte-incidencias'],
    queryFn: async () => {
      const [f, c] = await Promise.all(
        ['FALLIDO', 'EN_CONFLICTO'].map((estado) =>
          api.get<DocumentoDteListado[]>('/dte/documentos', { query: { empresaId: e, estado } }),
        ),
      );
      return [...f!, ...c!];
    },
    enabled: puede('dte.ver'),
    staleTime: 30_000,
    retry: false,
  });

  const pendientes = (
    [
      puede('ventas.nota.aprobar') && {
        id: 'firmas',
        texto: 'Notas esperando tu firma',
        n: firmas.data?.items.length,
        tono: 'firma' as const,
        alAbrir: () => abrirRuta('ventas/firmas'),
      },
      puede('entidades.ficha.aprobar_cambio') && {
        id: 'solicitudes',
        texto: 'Cambios de ficha por aprobar',
        n: solicitudes.data?.length,
        tono: 'firma' as const,
        alAbrir: () => abrirRuta('ventas/solicitudes-cambio'),
      },
      puede('ventas.nota.ver') && {
        id: 'borradores',
        texto: 'Notas en borrador (sin confirmar)',
        n: borradores.data?.items.length,
        alAbrir: () => abrirNotas('BORRADOR'),
      },
      puede('ventas.nota.ver') &&
        puede('ventas.despacho.ver') && {
          id: 'confirmadas',
          texto: 'Notas confirmadas por despachar',
          n: confirmadas.data?.items.length,
          alAbrir: () => abrirNotas('CONFIRMADA'),
        },
      puede('dte.ver') && {
        id: 'dte',
        texto: 'Documentos tributarios con incidencia',
        n: dte.data?.length,
        tono: 'malo' as const,
        alAbrir: () => abrirRuta('dte/incidencias'),
      },
    ] as Array<Linea | false>
  ).filter((x): x is Linea => !!x && !!x.n);

  return (
    <div className="pantalla inicio-tranquilo" data-pantalla="inicio">
      <div className="tarjeta-inicio">
        <div className="logo-inicio" aria-hidden>
          MADECI
          <span>Sistema de gestión</span>
        </div>
        <div className="listas-inicio">
          {pendientes.length > 0 && (
            <section aria-label="Pendientes">
              <h3>Pendientes</h3>
              <ul>
                {pendientes.map((p) => (
                  <li key={p.id}>
                    <button type="button" data-pendiente={p.id} onClick={p.alAbrir}>
                      <span className="texto">{p.texto}</span>
                      <b className={`n${p.tono ? ` tono-${p.tono}` : ''}`}>{cuenta(p.n!)}</b>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}
          {recientes.length > 0 && (
            <section aria-label="Recientes">
              <h3>Recientes</h3>
              <ul>
                {recientes.slice(0, 8).map((r) => (
                  <li key={`${r.tipo}:${r.id}`}>
                    <button type="button" data-reciente={r.tipo} onClick={() => alAbrirReciente(r)}>
                      <span className="tipo">
                        {r.tipo === 'nota' ? 'NV' : r.tipo === 'producto' ? 'Producto' : 'Cliente'}
                      </span>
                      <span className="texto">{r.titulo}</span>
                      <span className="cuando">{haceCuanto(r.en)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
        <p className="ayuda-inicio apagado">
          Elige una función en el menú o en la barra de íconos · <kbd>Ctrl+K</kbd> busca o ejecuta
          un comando · <kbd>F1</kbd> atajos
        </p>
      </div>
    </div>
  );
}

function haceCuanto(iso: string) {
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (min < 1) return 'recién';
  if (min < 60) return `hace ${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `hace ${h} h`;
  return new Date(iso).toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit' });
}
